import { format } from 'date-fns'
import { omit } from 'lodash'
import { isRunningLocally } from 'src/helpers'
import { sendLog } from './sentry'

const isDev = process.env.NODE_ENV === 'development' || isRunningLocally()

export interface StructuredMessage {
  msg: string
  error?: any
  [key: string]: any
}

type Message = string | StructuredMessage

const selectMessage = (msg: Message) =>
  typeof msg === 'string' ? msg : msg.msg

const selectData = (msg: Message) =>
  typeof msg === 'string' ? {} : omit(msg, ['msg', 'error'])

const selectError = (msg: Message) => {
  if (typeof msg === 'string') {
    return new Error(msg)
  }
  if (msg.error instanceof Error) {
    return msg.error
  }
  return new Error(msg.msg)
}

const formatData = (data: object) =>
  Object.entries(data).reduce(
    (acc, [k, v]) => `${acc} ${k}=${JSON.stringify(v)}`,
    '',
  )

const now = () => format(new Date(), 'HH:mm:ss.SSS')

const formatConsoleMessage = (moduleName: string, msg: Message) => {
  const title = selectMessage(msg)
  const data = selectData(msg)
  return `[${now()}] [${moduleName}] ${title}.${formatData(data)}`
}

const info = (moduleName: string, msg: Message) => {
  if (isDev && !isRunningLocally()) {
    // eslint-disable-next-line no-console
    return console.info(formatConsoleMessage(moduleName, msg))
  }
  const message = selectMessage(msg)
  const data = selectData(msg)

  sendLog({
    level: 'info',
    module: moduleName,
    message,
    data,
  })
}

const warn = (moduleName: string, msg: Message) => {
  if (isDev) {
    return console.warn(formatConsoleMessage(moduleName, msg))
  }
  const message = selectMessage(msg)
  const data = selectData(msg)

  if (typeof msg !== 'string' && msg.error) {
    data.error = selectError(msg).message
  }

  sendLog({
    level: 'warn',
    module: moduleName,
    message,
    data,
  })
}

const event = (moduleName: string, msg: Message) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    return console.log(formatConsoleMessage(moduleName, msg))
  }
  const message = selectMessage(msg)
  const data = selectData(msg)

  sendLog({
    level: 'info',
    module: moduleName,
    message,
    data,
  })
}

const screen = (moduleName: string, msg: Message) => {
  if (isDev) {
    // eslint-disable-next-line no-console
    return console.log(formatConsoleMessage(moduleName, msg))
  }
  const message = selectMessage(msg)
  const data = selectData(msg)

  sendLog({
    level: 'info',
    module: moduleName,
    message,
    data,
  })
}

const error = (moduleName: string, msg: Message) => {
  if (isDev) {
    if (typeof msg !== 'string') {
      console.error(formatConsoleMessage(moduleName, msg))
      console.error(msg.error)
    } else {
      console.error(msg)
    }
    return
  }
  const message = selectMessage(msg)
  const err = selectError(msg)
  const data = selectData(msg)

  sendLog({
    level: 'error',
    module: moduleName,
    message,
    data,
    error: err,
  })
}

export const createLogger = (moduleName: string) => ({
  screen: (msg: Message) => screen(moduleName, msg),
  event: (msg: Message) => event(moduleName, msg),
  info: (msg: Message) => info(moduleName, msg),
  warn: (msg: Message) => warn(moduleName, msg),
  error: (msg: Message) => error(moduleName, msg),
  //Debug only when running locally
  debug: (msg: Message) =>
    isRunningLocally() ? info(moduleName, msg) : undefined,
})

export type Logger = ReturnType<typeof createLogger>
